/** A local selection of tenants seeded from the console-wide scope — the multi
 *  counterpart of `useScopedTenantState`, for the state-held lists that take
 *  several tenants (plan C6, `<TenantPicker multiple>`).
 *
 * The selection FOLLOWS the whole scope until the user picks something here;
 * after that it is theirs and a scope change leaves it alone (clearing it
 * counts — "all tenants, on this page" is a choice). `reset()` hands control
 * back to the scope.
 *
 * A sentinel (System, id 0) is a value like any other here: `[SYSTEM]`, never
 * mixed with tenants — the picker keeps it exclusive (D3).
 */
import { useCallback, useRef, useState } from 'react';

import type { TenantPickerValue } from '@/components/TenantPicker/TenantPicker';

import { formatTenantIds } from './tenantIds';
import { useTenantScopeList } from './tenantScope';
import { useTenantScopeConsumer } from './useTenantScopeConsumer';

export type UseScopedTenantsState = [
  values: TenantPickerValue[],
  setValues: (ts: TenantPickerValue[]) => void,
  /** Forget the local choice and go back to following the console scope. */
  reset: () => void,
];

export function useScopedTenantsState(seed?: TenantPickerValue[]): UseScopedTenantsState {
  const scopeList = useTenantScopeList();
  useTenantScopeConsumer(true, 'multi');

  const [values, setValuesState] = useState<TenantPickerValue[]>(() => seed ?? [...scopeList]);
  // A caller-supplied seed is already a deliberate choice, so the scope does
  // not override it.
  const touched = useRef(seed !== undefined);

  // Follow a scope change during render rather than in an effect — no flash of
  // the old tenants, no extra render (the "adjust state on change" pattern).
  const scopeKey = formatTenantIds(scopeList.map((s) => s.id));
  const lastScopeKey = useRef(scopeKey);
  if (lastScopeKey.current !== scopeKey) {
    lastScopeKey.current = scopeKey;
    if (!touched.current) setValuesState([...scopeList]);
  }

  const setValues = useCallback((ts: TenantPickerValue[]) => {
    touched.current = true;
    setValuesState(ts);
  }, []);

  const reset = useCallback(() => {
    touched.current = false;
    setValuesState([...scopeList]);
  }, [scopeList]);

  return [values, setValues, reset];
}
